import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PALETTE } from "../tokens";
import { useAuth } from "../context/AuthContext";
import { dailyCheckinApi } from "../api/dailyCheckin";
import { DOMAIN_WORD, DOMAIN_META } from "../utils/domainLabels";

/* ──────────────────────────────────────────────────────────────────
 * HistoricalCheckIn
 *
 * Read-only view of a past day's check-in context, rendered when
 * the CheckIn screen is opened with a ``?date=YYYY-MM-DD`` query
 * param that resolves to a date other than today. Reached from
 * Intelligence Feed CTAs that anchor at a specific date — e.g.
 * "Resting HR spike on Tuesday → Check your journal from that day"
 * routes here so the user can see what they wrote on Tuesday
 * without having to scroll calendar by hand.
 *
 * Surfaces four panels, each independently optional:
 *   • Journal — that day's brain dump
 *   • Domain scores — bio/psych/beh + RCI total from the snapshot
 *   • Wearable — HRV / sleep / readiness / resting HR
 *   • Protocol activity — every protocol_inputs row for the day
 *
 * No submit button — this view is for reading, not editing. The
 * back-arrow returns to the Intelligence feed (the typical entry
 * point) via the browser history.
 * ────────────────────────────────────────────────────────────────── */

const STYLES = `
  .hist-root { font-family: 'DM Sans', sans-serif; background: ${PALETTE.bg}; min-height: 100vh; color: ${PALETTE.text}; max-width: 460px; margin: 0 auto; padding-bottom: 80px; }
  .hist-serif { font-family: 'Playfair Display', Georgia, serif; }

  .hist-section {
    background: #FDFAF6;
    border: 1px solid ${PALETTE.border};
    border-radius: 14px;
    padding: 18px 18px;
    margin: 0 24px 14px;
    box-shadow: 0 1px 8px rgba(28,24,20,0.04);
  }
  .hist-section-label {
    font-size: 9px; letter-spacing: 0.14em; text-transform: uppercase;
    color: ${PALETTE.primary}; font-weight: 600;
    margin-bottom: 10px;
  }
  .hist-empty {
    font-size: 12px; color: ${PALETTE.textMute};
    font-style: italic; font-weight: 300;
  }
  .hist-back {
    display: inline-flex; align-items: center; gap: 6px;
    background: none; border: none; padding: 8px 0;
    color: ${PALETTE.text}; font-size: 13px;
    cursor: pointer; min-height: 44px;
  }

  @keyframes histFadeUp {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .hist-fade { animation: histFadeUp 0.4s ease forwards; }
`;


function formatLongDate(isoDate) {
  if (!isoDate) return "";
  try {
    // Parse as local-date midnight so the formatted string matches
    // the user's calendar, not UTC. ``new Date('2026-05-12')`` is
    // interpreted as UTC midnight, which shifts the day backwards
    // west of UTC — explicit Y/M/D construction avoids that.
    const [y, m, d] = isoDate.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return isoDate;
  }
}


function pct(v) {
  if (v == null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${Math.round(n * 100)}`;
}


function formatNumber(v, decimals = 0) {
  if (v == null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return decimals > 0 ? n.toFixed(decimals) : `${Math.round(n)}`;
}


function formatTime(iso) {
  if (!iso) return "";
  try {
    const dt = new Date(iso);
    return dt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}


export default function HistoricalCheckIn({ date }) {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [data, setData] = useState(null); // null = loading
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!token || !date) return;
    let cancelled = false;
    dailyCheckinApi
      .history(token, date)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setData({}); // surface the empty-section copy
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, date]);

  const heading = useMemo(() => `Your check-in on ${formatLongDate(date)}`, [date]);

  const isLoading = data === null;
  const journalText = data?.journal_text;
  const hasScores = data && (data.bio_score != null || data.psych_score != null || data.beh_score != null);
  const hasWearable = data && (data.hrv != null || data.sleep_score != null || data.readiness != null || data.resting_hr != null);
  const protocolRows = data?.protocol_inputs || [];

  return (
    <>
      <style>{STYLES}</style>
      <div className="hist-root">
        {/* Header */}
        <div style={{ padding: "20px 24px 6px" }}>
          <button className="hist-back" onClick={() => navigate(-1)} aria-label="Back">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            <span style={{ fontSize: 13 }}>Back</span>
          </button>
        </div>

        <div className="hist-fade" style={{ padding: "8px 24px 22px" }}>
          <p style={{ fontSize: 10, color: PALETTE.primary, letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 500, marginBottom: 10 }}>
            History
          </p>
          <h1
            className="hist-serif"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontStyle: "italic", fontSize: 26, fontWeight: 400,
              color: "#1C1814", lineHeight: 1.25,
            }}
          >
            {heading}
          </h1>
          {error && (
            <p style={{ fontSize: 12, color: PALETTE.textMute, marginTop: 8 }}>
              Couldn't reach the server. Showing whatever loaded.
            </p>
          )}
        </div>

        {/* Loading skeleton */}
        {isLoading && (
          <>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="hist-section" style={{ height: 96 }} />
            ))}
          </>
        )}

        {/* Journal */}
        {!isLoading && (
          <div className="hist-section hist-fade">
            <div className="hist-section-label">Journal</div>
            {journalText ? (
              <p style={{
                fontSize: 14, lineHeight: 1.65,
                color: "#1C1814", fontWeight: 400,
                whiteSpace: "pre-wrap",
              }}>
                {journalText}
              </p>
            ) : (
              <p className="hist-empty">No journal entry for this day.</p>
            )}
          </div>
        )}

        {/* Domain scores */}
        {!isLoading && (
          <div className="hist-section hist-fade">
            <div className="hist-section-label">Domain scores</div>
            {hasScores ? (
              <>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  {DOMAIN_META.map((d) => ({
                    code: d.code,
                    value: data[`${d.key}_score`],
                  })).map(({ code, value }) => (
                    <div
                      key={code}
                      style={{
                        flex: "1 1 90px",
                        minWidth: 90,
                        padding: "10px 12px",
                        background: PALETTE.surface,
                        border: `1px solid ${PALETTE.border}`,
                        borderRadius: 10,
                      }}
                    >
                      <div style={{ fontSize: 9, color: PALETTE.primary, letterSpacing: "0.12em", fontWeight: 600, marginBottom: 4 }}>{DOMAIN_WORD[code] || code}</div>
                      <div className="hist-serif" style={{ fontSize: 22, color: "#1C1814", fontStyle: "italic" }}>{pct(value)}</div>
                    </div>
                  ))}
                </div>
                {data.rci_total != null && (
                  <p style={{ marginTop: 14, fontSize: 12, color: PALETTE.textMid }}>
                    Signal score: <span style={{ color: PALETTE.text, fontWeight: 500 }}>{pct(data.rci_total)}</span>
                  </p>
                )}
              </>
            ) : (
              <p className="hist-empty">No signal recorded for this day.</p>
            )}
          </div>
        )}

        {/* Wearable */}
        {!isLoading && (
          <div className="hist-section hist-fade">
            <div className="hist-section-label">Wearable</div>
            {hasWearable ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {[
                  { label: "HRV", value: data.hrv == null ? null : `${formatNumber(data.hrv)} ms` },
                  { label: "Sleep", value: data.sleep_score == null ? null : `${formatNumber(data.sleep_score)}` },
                  { label: "Resting HR", value: data.resting_hr == null ? null : `${formatNumber(data.resting_hr)} bpm` },
                  { label: "Readiness", value: data.readiness == null ? null : `${formatNumber(data.readiness)}` },
                ].map(({ label, value }) => (
                  <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${PALETTE.border}` }}>
                    <span style={{ fontSize: 12.5, color: PALETTE.textMid, fontWeight: 300 }}>{label}</span>
                    <span style={{ fontSize: 13, color: value == null ? PALETTE.textMute : "#1C1814", fontStyle: value == null ? "italic" : "normal", fontWeight: 400 }}>
                      {value ?? "—"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="hist-empty">No wearable data for this day.</p>
            )}
          </div>
        )}

        {/* Protocol activity */}
        {!isLoading && (
          <div className="hist-section hist-fade">
            <div className="hist-section-label">Protocol activity</div>
            {protocolRows.length === 0 ? (
              <p className="hist-empty">No protocol activity logged on this day.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {protocolRows.map((row) => (
                  <div key={row.id} style={{ borderLeft: `2px solid ${PALETTE.primary}`, paddingLeft: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 10, color: PALETTE.primary, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 500 }}>
                        {row.step_type?.replace(/_/g, " ") || "Input"}
                      </span>
                      <span style={{ fontSize: 10.5, color: PALETTE.textFaint }}>{formatTime(row.created_at)}</span>
                    </div>
                    {row.protocol_id && (
                      <div style={{ fontSize: 11, color: PALETTE.textMute, marginBottom: 4 }}>
                        {row.protocol_id.replace(/-/g, " ")}
                      </div>
                    )}
                    <p style={{ fontSize: 13, color: "#1C1814", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
                      {row.input_text}
                    </p>
                    {row.response_text && (
                      <p style={{ marginTop: 6, fontSize: 12.5, color: PALETTE.textMid, fontStyle: "italic", whiteSpace: "pre-wrap" }}>
                        {row.response_text}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
